import { createBrowserRouter, Navigate, redirect } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { loader as calendarLoader } from "@/components/bookings/calendar/BookingsListCalendar";
import { loader as checkoutLoader } from "@/components/bookings/checkout/BookingCheckout";
import BookingCheckoutWrapper from "@/components/bookings/checkout/BookingCheckoutWrapper";
import { loader as bookingsLoader } from "@/components/bookings/current/BookingsListCurrent";
import BookingDetailsWrapper from "@/components/bookings/details/BookingDetailsWrapper";
import { loader as instructionsLoader } from "@/components/bookings/details/BookingInstructions";
import BookingInstructions from "@/components/bookings/details/BookingInstructions";
import { loader as overviewLoader } from "@/components/bookings/details/BookingOverview";
import BookingOverview from "@/components/bookings/details/BookingOverview";
import { loader as postsNotesLoader } from "@/components/bookings/details/BookingPostsNotes";
import BookingPostsNotes from "@/components/bookings/details/BookingPostsNotes";
import ErrorBoundary from "@/components/errors/ErrorBoundary";
import MenuFull from "@/components/menu/MenuFull";
import BookingsPage from "@/pages/BookingsPage";
import CalendarPage from "@/pages/CalendarPage";
import LoginPage from "@/pages/LoginPage";
import { useAuthStore } from "@/store/AuthStore";
import MainLayout from "@/layouts/MainLayout";
import { ApiService } from "@/api/services/ApiService";
import { msalInstance, msalReady } from "@/lib/msal-config";

// ---------------------- Helpers

async function getIsAuthenticated(): Promise<boolean> {
  await msalReady;
  await msalInstance.handleRedirectPromise();
  return msalInstance.getAllAccounts().length > 0;
}

async function ensureUserData(): Promise<void> {
  const store = useAuthStore.getState();

  if (store.userResourceId) return;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return;

  const upn = accounts[0].username;

  try {
    const user = await ApiService.user.getUser(upn);
    store.setUserResourceId(user.bookableResourceId);
    store.setUserSystemId(user.systemUserId);
    store.setUserName(user.fullName);
    store.setUserEmail(user.internalEmailAddress);
  } catch {
    // User fetch failed — sign-out handles clean redirect if needed
  }
}

// ---------------------- Component

export const createRouter = (queryClient: QueryClient) => {
  // this is called whenever navigation is requested to any page other than Login
  const protectedLoader = async ({ request }: { request: Request }) => {
    const isAuthenticated = await getIsAuthenticated();

    // redirect to login if not authenticated
    if (!isAuthenticated) {
      const pathname = new URL(request.url).pathname;

      // this allows us to auto-navigate to a specific page (instead of the default) after login is successful
      if (pathname !== "/" && pathname !== "/login" && pathname !== "/menu") {
        throw redirect(`/login?from=${pathname}`);
      }

      throw redirect("/login");
    }

    await ensureUserData();

    return null;
  };

  const loginLoader = async ({ request }: { request: Request }) => {
    const isAuthenticated = await getIsAuthenticated();

    // redirect to a requested page (default or other) on successful login
    if (isAuthenticated) {
      // Check if there's a specific route to return to
      const url = new URL(request.url);
      const from = url.searchParams.get("from");

      if (from && from !== "/menu") {
        throw redirect(from);
      }

      // Otherwise go to default route
      throw redirect("/bookings");
    }

    return null;
  };

  // --------- Router

  return createBrowserRouter([
    {
      path: "/",
      loader: async () => {
        const isAuthenticated = await getIsAuthenticated();
        throw redirect(isAuthenticated ? "/bookings" : "/login");
      },
      element: <></>,
    },
    {
      path: "/login",
      element: <LoginPage />,
      loader: loginLoader,
    },
    {
      path: "/signin-microsoft",
      element: (
        <Navigate
          to="/"
          replace
        />
      ),
    },
    {
      element: <MainLayout />,
      loader: protectedLoader,
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: "menu",
          element: <MenuFull />,
        },
        {
          path: "/bookings",
          element: <BookingsPage />,
          loader: bookingsLoader(queryClient),
          children: [
            {
              path: "details/:id",
              element: <BookingDetailsWrapper parentRoute="/bookings" />,
              children: [
                {
                  path: "",
                  element: (
                    <Navigate
                      to="overview"
                      replace
                    />
                  ),
                },
                {
                  path: "overview",
                  loader: overviewLoader(queryClient),
                  element: <BookingOverview />,
                },
                {
                  path: "instructions",
                  loader: instructionsLoader(queryClient),
                  element: <BookingInstructions />,
                },
                {
                  path: "posts-notes",
                  loader: postsNotesLoader(queryClient),
                  element: <BookingPostsNotes />,
                },
              ],
            },
            {
              path: "checkout/:id",
              element: <BookingCheckoutWrapper parentRoute="/bookings" />,
              loader: checkoutLoader(queryClient),
            },
          ],
        },
        {
          path: "/calendar",
          loader: calendarLoader(queryClient),
          element: <CalendarPage />,
          children: [
            {
              path: "details/:id",
              element: <BookingDetailsWrapper parentRoute="/calendar" />,
              children: [
                {
                  path: "",
                  element: (
                    <Navigate
                      to="overview"
                      replace
                    />
                  ),
                },
                {
                  path: "overview",
                  loader: overviewLoader(queryClient),
                  element: <BookingOverview />,
                },
                {
                  path: "instructions",
                  loader: instructionsLoader(queryClient),
                  element: <BookingInstructions />,
                },
                {
                  path: "posts-notes",
                  loader: postsNotesLoader(queryClient),
                  element: <BookingPostsNotes />,
                },
              ],
            },
            {
              path: "checkout/:id",
              element: <BookingCheckoutWrapper parentRoute="/calendar" />,
              loader: checkoutLoader(queryClient),
            },
          ],
        },
        // {
        //   path: "/forms",
        //   element: <FormsPage />,
        // },
      ],
    },
    {
      path: "*",
      element: (
        <Navigate
          to="/"
          replace
        />
      ),
    },
  ]);
};
